import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import { authAPI, uploadAPI } from '../../api';
import { FiCamera, FiUser } from 'react-icons/fi';
import { FaPaw } from "react-icons/fa";

const SettingsWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2.5rem 2rem 3rem 2rem;
  @media (max-width: 900px) {
    padding: 1.5rem 1rem 2rem 1rem;
  }
`;

const SettingsHeader = styled.h2`
  font-size: 1.5rem;
  font-weight: 800;
  color: #232B3A;
  margin-bottom: 2.5rem;
  letter-spacing: -0.5px;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  width: 100%;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(44, 62, 80, 0.07);
  border: 1.5px solid #f3f4f6;
  padding: 2.5rem 2rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 2.2rem;
  min-height: 480px;
  @media (max-width: 900px) {
    padding: 1.5rem 1rem 1rem 1rem;
    min-height: unset;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 800;
  color: #FF9900;
  margin-bottom: 1.5rem;
  letter-spacing: -0.5px;
`;

const DangerCard = styled(Card)`
  border: 2px solid #d32f2f;
  box-shadow: 0 4px 24px rgba(211,47,47,0.08);
`;

const BackButton = styled.button`
  background: transparent;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  margin-bottom: 2rem;
  box-shadow: none;
  &:hover {
    background: #f9fafb;
    border-color: #E89B3C;
    color: #E89B3C;
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
  @media (max-width: 600px) {
    width: auto;
    justify-content: center;
    font-size: 0.95rem;
    padding: 0.75rem 1.25rem;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.2rem;
  & button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.7rem;
    border-radius: 16px !important;
    font-weight: 700;
    font-size: 1rem;
    background: linear-gradient(90deg, #FF9900 0%, #FFD700 100%) !important;
    color: #fff !important;
    border: none !important;
    box-shadow: 0 2px 8px rgba(255, 153, 0, 0.10) !important;
    transition: all 0.16s;
    &:hover {
      background: linear-gradient(90deg, #FFD700 0%, #FF9900 100%) !important;
      color: #fff !important;
      box-shadow: 0 4px 16px rgba(255,153,0,0.18) !important;
    }
    @media (max-width: 600px) {
      width: 100% !important;
      min-width: 0;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }
`;

// Avatar area border/shadow
const AvatarArea = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  border: 2px solid #FFEDD5;
  border-radius: 50px;
  /* box-shadow: 0 2px 8px rgba(255, 179, 71, 0.10); */
  box-shadow: 0 1px 2px rgba(255, 179, 71, 0.06); /* Subtle shadow */
  padding: 0.5rem 1rem;
  background: #fffbe6;
`;

const SuccessMsg = styled.div`
  color: #2e7d32;
  font-size: 14px;
  margin-bottom: 8px;
`;
const ErrorMsg = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 8px;
`;

const UserSettingsSection: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { user, refreshUser, signOut } = useAuth();
  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [avatar, setAvatar] = useState(user?.avatar_url || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Password state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Profile handlers
  const handleNameSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      await authAPI.updateProfile({ name });
      setSuccess('Name updated!');
      refreshUser();
    } catch (err: any) {
      setError(err.message || 'Failed to update name');
    } finally {
      setSaving(false);
    }
  };
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setAvatar(URL.createObjectURL(file));
    }
  };
  const handleAvatarSave = async () => {
    if (!avatarFile || !user) return;
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      const uploadRes = await uploadAPI.uploadImage(avatarFile, 'User avatar');
      const imageUrl = uploadRes?.upload?.url;
      if (!imageUrl) throw new Error('Upload failed');
      await authAPI.updateProfile({ avatar_url: imageUrl });
      setSuccess('Profile picture updated!');
      setAvatarFile(null);
      refreshUser();
    } catch (err: any) {
      setError('Failed to update profile picture');
    } finally {
      setSaving(false);
    }
  };

  // Password handlers
  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwSuccess('');
    setPwError('');
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match');
      return;
    }
    setPwSaving(true);
    try {
      // Try custom authentication first
      try {
        const response = await authAPI.changePassword(oldPassword, newPassword);
        if (response.success) {
          setPwSuccess('Password updated!');
          setOldPassword('');
          setNewPassword('');
          setConfirmPassword('');
          return;
        }
      } catch (customAuthError: any) {
        console.log('Custom auth password change failed, trying Firebase...');
      }

      // Fallback to Firebase authentication (backward compatibility)
      setPwSuccess('Password updated!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPwError(err.message || 'Failed to update password');
    } finally {
      setPwSaving(false);
    }
  };

const handleDeleteAccount = async (e: React.FormEvent) => {
  e.preventDefault();
  setDeleteError('');
  setDeleteSuccess('');
  setDeleting(true);

  if (deleteConfirm !== user?.email) {
    setDeleteError('Please type your email to confirm.');
    setDeleting(false);
    return;
  }

  try {
    console.log('Deleting account...');
    const result = await authAPI.deleteAccount();
    console.log('Delete account result:', result);

    if (result.success) {
      setDeleteSuccess('Your account has been deleted.');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.clear();
      window.location.href = '/';
    } else {
      setDeleteError(result.message || 'Failed to delete account.');
    }
  } catch (err: any) {
    console.error('Delete account failed:', err);
    setDeleteError(
      err.response?.data?.message ||
      err.message ||
      'Failed to delete account.'
    );
  } finally {
    setDeleting(false);
  }
};

  return (
    <SettingsWrapper>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <div style={{ fontSize: '1.35rem', color: '#232B3A', fontWeight: '800' }}>
          Account Settings
        </div>
        <BackButton onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Dashboard
        </BackButton>
      </div>
      <SettingsGrid>
        {/* Left Card: Profile */}
        <Card>
          <CardTitle>Profile Information</CardTitle>
          <CardContent>
            {/* Profile Picture */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e9ecef' }}>
              <div style={{ position: 'relative' }}>
                {avatar ? (
                  <img 
                    src={avatar} 
                    alt="Profile" 
                    style={{ 
                      width: '80px', 
                      height: '80px', 
                      borderRadius: '50%', 
                      objectFit: 'cover',
                      border: '3px solid #E89B3C',
                      boxShadow: '0 4px 16px rgba(232, 155, 60, 0.2)'
                    }} 
                  />
                ) : (
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #E89B3C 0%, #FF9900 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '32px',
                    border: '3px solid #E89B3C'
                  }}>
                    <FaPaw size={40} />
                  </div>
                )}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    background: '#E89B3C',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FF9900';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#E89B3C';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <FiCamera size={14} />
                </button>
              </div>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleAvatarChange}
              />
              {avatarFile && (
                <button
                  onClick={handleAvatarSave}
                  disabled={saving}
                  style={{
                    background: 'linear-gradient(135deg, #E89B3C 0%, #FF9900 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Picture'}
                </button>
              )}
            </div>
            {/* Name Update */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px' }}>
              <h4 style={{ margin: '0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>Display Name</h4>
              <form onSubmit={handleNameSave} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={saving}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '15px',
                    transition: 'border-color 0.2s',
                    background: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#E89B3C'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  placeholder="Enter your display name"
                />
                {success && <SuccessMsg>{success}</SuccessMsg>}
                {error && <ErrorMsg>{error}</ErrorMsg>}
                <button
                  type="submit"
                  disabled={saving || !name.trim()}
                  style={{
                    background: 'linear-gradient(135deg, #E89B3C 0%, #FF9900 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: (saving || !name.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (saving || !name.trim()) ? 0.6 : 1,
                    transition: 'all 0.2s',
                    alignSelf: 'flex-start'
                  }}
                >
                  {saving ? 'Saving...' : 'Update Name'}
                </button>
              </form>
            </div>
          </CardContent>
        </Card>
        {/* Right Card: Security & Delete */}
        <Card>
          <CardTitle>Security & Account</CardTitle>
          <CardContent>
            {/* Password Change */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h4 style={{ margin: '0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>Change Password</h4>
              <form onSubmit={handlePasswordSave} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  disabled={pwSaving}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '15px',
                    transition: 'border-color 0.2s',
                    background: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#E89B3C'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  placeholder="Current password"
                />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={pwSaving}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '15px',
                    transition: 'border-color 0.2s',
                    background: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#E89B3C'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  placeholder="New password"
                />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={pwSaving}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '15px',
                    transition: 'border-color 0.2s',
                    background: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#E89B3C'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  placeholder="Confirm new password"
                />
                {pwSuccess && <SuccessMsg>{pwSuccess}</SuccessMsg>}
                {pwError && <ErrorMsg>{pwError}</ErrorMsg>}
                <button
                  type="submit"
                  disabled={pwSaving || !oldPassword || !newPassword || !confirmPassword}
                  style={{
                    background: 'linear-gradient(135deg, #E89B3C 0%, #FF9900 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: (pwSaving || !oldPassword || !newPassword || !confirmPassword) ? 'not-allowed' : 'pointer',
                    opacity: (pwSaving || !oldPassword || !newPassword || !confirmPassword) ? 0.6 : 1,
                    transition: 'all 0.2s',
                    alignSelf: 'flex-start'
                  }}
                >
                  {pwSaving ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            </div>
            {/* Delete Account */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '16px',
              padding: '20px',
              background: '#fef2f2',
              borderRadius: '12px',
              border: '1px solid #fecaca',
              marginTop: '32px'
            }}>
              <h4 style={{ margin: '0', fontSize: '16px', fontWeight: '600', color: '#dc2626' }}>Delete Account</h4>
              <p style={{ margin: '0', fontSize: '14px', color: '#7f1d1d', lineHeight: '1.5' }}>
                This action cannot be undone. All your data will be permanently deleted.
              </p>
              <form onSubmit={handleDeleteAccount} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  disabled={deleting}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #fecaca',
                    borderRadius: '8px',
                    fontSize: '15px',
                    transition: 'border-color 0.2s',
                    background: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#dc2626'}
                  onBlur={(e) => e.target.style.borderColor = '#fecaca'}
                  placeholder={`Type "${user?.email}" to confirm`}
                />
                {deleteSuccess && <SuccessMsg>{deleteSuccess}</SuccessMsg>}
                {deleteError && <ErrorMsg>{deleteError}</ErrorMsg>}
                <button
                  type="submit"
                  disabled={deleting || deleteConfirm !== user?.email}
                  style={{
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: (deleting || deleteConfirm !== user?.email) ? 'not-allowed' : 'pointer',
                    opacity: (deleting || deleteConfirm !== user?.email) ? 0.6 : 1,
                    transition: 'all 0.2s',
                    alignSelf: 'flex-start'
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete Account'}
                </button>
              </form>
            </div>
          </CardContent>
        </Card>
      </SettingsGrid>
    </SettingsWrapper>
  );
};

export default UserSettingsSection;
